<ppl-app-container [showCopyright]="false">
  <div class="align-in-center">
    <ppl-box-container-with-title
      [title]="title$ | async"
      [description]="description$ | async"
      [small]="true">
      @if (error$ | async; as error) {
        <ppl-error-box>
          @switch (error.error) {
            @case (TwoFactorError.TOO_MANY_REQUESTS) {
              {{ getRetryAfterMessage(error) }}
            }
            @default {
              <ng-container i18n="@@Unknown_error.">Unknown error.</ng-container>
            }
          }
        </ppl-error-box>
      }
      @switch (mode$ | async) {
        @case (TwoFactorMode.OPTIONS) {
          <div
            class="content"
            [formGroup]="optionsForm">
            <ppl-two-factor-method-selector
              formControlName="methodId"
              [methods]="optionsMethods$ | async"></ppl-two-factor-method-selector>
            <div
              class="info"
              i18n="@@TwoFactorVerificationSelectOption">
              Select a 2-step verification method you prefer and press
              <strong>Continue</strong>.
            </div>
            <div class="action-buttons">
              <button
                pplButton
                class="submit-button"
                (click)="onOptionsSetTwoFactorMethod()"
                i18n="@@Continue">
                Continue
              </button>
              <button
                pplLink
                class="link-button"
                (click)="onOptionsBackButtonClick()">
                <ng-container *ngTemplateOutlet="backButtonTextTemplate"></ng-container>
              </button>
            </div>
          </div>
        }
        @case (TwoFactorMode.SETUP) {
          <div class="content">
            <ppl-edit-email
              [user]="user$ | async"
              (edit)="onBackToLogin()"></ppl-edit-email>
            <div class="click-to-setup">
              @if (currentSetupMethodType$ | async; as currentSetupMethodType) {
                <ppl-icon
                  [svgIcon]="methodTypeToIconMap[currentSetupMethodType]"
                  height="48px"
                  width="48px"></ppl-icon>
                <div class="setup-button-wrapper">
                  <button
                    pplButton
                    class="setup-button"
                    i18n="@@Click_To_Set_Up"
                    (click)="onSetupMethodType(currentSetupMethodType)">
                    Click To Set Up
                  </button>
                  @if (setuping$ | async) {
                    <ppl-loading-overlay
                      [visible]="true"
                      [inverse]="true"></ppl-loading-overlay>
                  }
                </div>
              } @else {
                <span i18n="@@No_2-Step_verification_method_selected">No 2-Step verification method selected</span>
              }
            </div>
            <div
              class="info"
              i18n="@@2-Step_verification_was_enabled_to_improve_your_account_security.">
              2-Step verification was enabled to improve your account security.
            </div>
            <div
              class="info"
              i18n="@@TwoFactorSetupClickToSetupText">
              To continue, <strong>Click To Set Up</strong> and complete a simple verification process.
            </div>
            <div
              class="info"
              i18n="@@It_takes_a_few_seconds.">
              It takes a few seconds.
            </div>
            <div class="action-buttons">
              <div class="action-buttons-row">
                <button
                  pplLink
                  class="link-button"
                  (click)="onBackButtonClick()">
                  <ng-container *ngTemplateOutlet="backButtonTextTemplate"></ng-container>
                </button>
                @if ((setupMethodTypes$ | async).length) {
                  <button
                    pplLink
                    class="link-button"
                    [disabled]="setuping$ | async"
                    [pplPopover]="setupMoreOptionsPopover"
                    [pplPopoverAlignEnd]="true"
                    [pplPopoverDisabled]="setuping$ | async"
                    i18n="@@Show_more_options">
                    Show more options
                  </button>
                }
              </div>
            </div>
          </div>
        }
        @case (TwoFactorMode.VERIFY) {
          <div
            class="content"
            [formGroup]="verifyForm">
            <ppl-edit-email
              [user]="user$ | async"
              (edit)="onBackToLogin()"></ppl-edit-email>
            <div class="verification">
              <ppl-icon
                class="verification-method-icon"
                [svgIcon]="methodTypeToIconMap[(currentMethod$ | async)?.type]"
                height="48px"
                width="48px"></ppl-icon>
              <div>
                <ppl-input-container
                  label="Verification Code"
                  i18n-label="@@Verification_Code"
                  pplFormControlName="code">
                  <ppl-two-factor-verification-code
                    formControlName="code"
                    (submit)="onVerifyCodeClick()"></ppl-two-factor-verification-code>
                </ppl-input-container>
                @if (verifyResendCodeVisible$ | async) {
                  <div class="re-send-code-container">
                    <button
                      pplLink
                      class="link-button"
                      [disabled]="verifyResendCodeDisabled$ | async"
                      (click)="onVerifyResendCode()"
                      i18n="@@Re-send_code">
                      Re-send code
                    </button>
                    @if (verifyResendToggleIcon$ | async) {
                      <ppl-icon
                        svgIcon="checkmark-white"
                        height="14px"
                        width="14px"></ppl-icon>
                    }
                  </div>
                }
              </div>
            </div>
            @if (currentMethod$ | async; as currentMethod) {
              <div>
                @switch (currentMethod.type) {
                  @case (TwoFAType.BACKUP_CODES) {
                    <div
                      class="info"
                      i18n="@@TwoFactorScreenVerifyBackupCodesInfoMessage">
                      Enter one of the pre-generated {{ TWO_FACTOR_CODE_LENGTH }}-digit backup codes.
                    </div>
                  }
                  @case (TwoFAType.AUTH_APP) {
                    <div
                      class="info"
                      i18n="@@TwoFactorScreenVerifyAuthAppInfoMessage">
                      Enter the {{ TWO_FACTOR_CODE_LENGTH }}-digit verification code from your:
                    </div>
                    <div class="method-value">{{ currentMethodName$ | async }}</div>
                  }
                  @case (TwoFAType.DUMMY) {
                    <div
                      class="info"
                      i18n="@@Enter_the_following_code:">
                      Enter the following code:
                    </div>
                    <div class="method-value">{{ currentMethodValue$ | async }}</div>
                  }
                  @case (TwoFAType.EMAIL_MS_IDENTITY_CONFIRMATION) {
                    <div
                      class="info"
                      i18n="@@TwoFactorScreenVerifyDefaultInfoMessage">
                      Enter the {{ TWO_FACTOR_CODE_LENGTH }}-digit verification code sent to:
                    </div>
                    <div class="method-value">{{ currentMethodValue$ | async }}</div>
                    <div
                      class="info"
                      i18n="@@It_may_take_several_seconds_to_arrive.">
                      It may take several seconds to arrive.
                    </div>
                    <div
                      class="info"
                      i18n="@@TwoFactorScreenVerifyEmailMSIdentityConfirmationInfoMessage">
                      This has to be done only the first time you use your Microsoft Account to login.
                    </div>
                  }
                  @default {
                    <div
                      class="info"
                      i18n="@@TwoFactorScreenVerifyDefaultInfoMessage">
                      Enter the {{ TWO_FACTOR_CODE_LENGTH }}-digit verification code sent to:
                    </div>
                    <div class="method-value">{{ currentMethodValue$ | async }}</div>
                    <div
                      class="info"
                      i18n="@@It_may_take_several_seconds_to_arrive.">
                      It may take several seconds to arrive.
                    </div>
                  }
                }
              </div>
              @if (!rememberDisabledForTypes.includes(currentMethod.type)) {
                <ppl-checkbox
                  formControlName="remember"
                  i18n="@@Don't_ask_again_on_this_device"
                  >Don't ask again on this device</ppl-checkbox
                >
              }
            }
            <div class="action-buttons">
              <button
                pplButton
                class="submit-button"
                (click)="onVerifyCodeClick()"
                [disabled]="!(verification$ | async)"
                i18n="@@Continue">
                Continue
              </button>
              <div class="action-buttons-row">
                <button
                  pplLink
                  class="link-button"
                  (click)="onBackButtonClick()">
                  <ng-container *ngTemplateOutlet="backButtonTextTemplate"></ng-container>
                </button>
                @if ((currentMethod$ | async)?.type !== TwoFAType.EMAIL_MS_IDENTITY_CONFIRMATION) {
                  <button
                    pplLink
                    class="link-button"
                    [disabled]="verifyResendCodeDisabled$ | async"
                    (click)="onVerifyMoreOptionClick()">
                    <ng-container i18n="@@Try_more_options">Try more options</ng-container>
                    &gt;&gt;
                  </button>
                }
              </div>
            </div>
          </div>
        }
      }
      @if (loading$ | async) {
        <ppl-loading-overlay
          [large]="true"
          [visible]="true"
          [inverse]="true"></ppl-loading-overlay>
      }
    </ppl-box-container-with-title>
  </div>
</ppl-app-container>

<ng-template #backButtonTextTemplate> &lt;&lt; <ng-container i18n="@@Back">Back</ng-container></ng-template>

<ng-template #setupMoreOptionsPopover>
  <ppl-two-factor-method-type-selector-dropdown
    [value]="currentSetupMethodType$ | async"
    [types]="setupMethodTypes$ | async"
    (valueChange)="onSetupMethodTypeChange($event)"></ppl-two-factor-method-type-selector-dropdown>
</ng-template>
